/**
 * NoteEntryCard Component
 * Renders a single note entry (menu item + content + optional photo) inside a bordered card.
 * Used when creating or editing tasting notes.
 */

import Ionicons from '@expo/vector-icons/Ionicons';
import React, { useCallback } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';

import { MenuItem } from '@/types/menu-item';

import { PhotoAsset } from '../../check-in/types';
import { MenuItemSearchSection } from '../../check-in/components/menu-item-search-section';
import { PhotoSection } from '../../check-in/components/photo-section';
import { NoteEntryState } from '../types';

export interface NoteEntryCardProps {
  entry: NoteEntryState;
  entryIndex: number;
  businessId: string;
  businessName: string;
  isSubmitting: boolean;
  canRemove: boolean;
  onUpdate: (id: string, patch: Partial<NoteEntryState>) => void;
  onRemove: (id: string) => void;
}

export function NoteEntryCard({
  entry,
  entryIndex,
  businessId,
  businessName,
  isSubmitting,
  canRemove,
  onUpdate,
  onRemove,
}: NoteEntryCardProps) {
  const handleSelectMenuItem = useCallback(
    (menuItem: MenuItem) => {
      onUpdate(entry.id, { menuItem });
    },
    [entry.id, onUpdate]
  );

  const handleClearMenuItem = useCallback(() => {
    onUpdate(entry.id, { menuItem: null });
  }, [entry.id, onUpdate]);

  const handleContentChange = useCallback(
    (content: string) => {
      onUpdate(entry.id, { content });
    },
    [entry.id, onUpdate]
  );

  const handleAddPhoto = useCallback(
    (photo: PhotoAsset) => {
      onUpdate(entry.id, { photo });
    },
    [entry.id, onUpdate]
  );

  const handleRemovePhoto = useCallback(() => {
    onUpdate(entry.id, { photo: null });
  }, [entry.id, onUpdate]);

  const handleRemove = useCallback(() => {
    onRemove(entry.id);
  }, [entry.id, onRemove]);

  // Wrap single photo into array for PhotoSection compatibility
  const photos = entry.photo ? [entry.photo] : [];

  const handlePhotoRemove = useCallback(
    (_photoId: string) => {
      handleRemovePhoto();
    },
    [handleRemovePhoto]
  );

  return (
    <View className="border border-border rounded-xl p-4 mb-4">
      {/* Restaurant context */}
      <View className="flex-row items-center mb-2">
        <Ionicons name="location-outline" size={14} className="text-muted-foreground/70" />
        <Text className="text-xs text-muted-foreground/70 ml-1" numberOfLines={1}>
          {businessName}
        </Text>
      </View>

      {/* Entry header */}
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-sm font-medium text-muted-foreground">
          Note {entryIndex + 1}
        </Text>
        {canRemove && (
          <Pressable
            onPress={handleRemove}
            disabled={isSubmitting}
            hitSlop={8}
            accessibilityLabel={`Remove note ${entryIndex + 1}`}
            className="p-1"
          >
            <Ionicons name="close" size={18} className="text-muted-foreground" />
          </Pressable>
        )}
      </View>

      {/* Menu Item */}
      <MenuItemSearchSection
        businessId={businessId}
        businessName={businessName}
        selectedMenuItem={entry.menuItem}
        onSelect={handleSelectMenuItem}
        onClear={handleClearMenuItem}
      />

      {/* Content - show after menu item selected */}
      {entry.menuItem && (
        <View className="mb-4">
          <Text className="text-sm font-medium mb-2 text-muted-foreground">
            Tasting Notes
          </Text>
          <TextInput
            value={entry.content}
            onChangeText={handleContentChange}
            placeholder="What did you notice? Flavors, textures, what to try next time..."
            placeholderTextColor="#9CA3AF"
            multiline
            numberOfLines={4}
            editable={!isSubmitting}
            className="text-sm text-foreground bg-surface border border-border rounded-lg p-3"
            style={{ minHeight: 100, textAlignVertical: 'top' }}
            accessibilityLabel="Note content"
          />
        </View>
      )}

      {/* Divider */}
      {entry.menuItem && <View className="my-2 h-px bg-border/50" />}

      {/* Photo - show after menu item selected */}
      {entry.menuItem && (
        <PhotoSection
          photos={photos}
          onAdd={handleAddPhoto}
          onRemove={handlePhotoRemove}
          maxPhotos={1}
          disabled={isSubmitting}
        />
      )}
    </View>
  );
}

export default NoteEntryCard;
